﻿/**
 * availability-qty-check.js
 *
 * Injects the correct 'availability' property into /cart/change.js BEFORE Shopify
 * processes it, so the section re-render Horizon fires immediately after the response
 * already shows the correct badge -- no second round-trip needed.
 *
 * Reads only from properties already stored on the cart line item:
 *   _avail_stock      -- inventory_quantity at add-to-cart time (set by buy-buttons.liquid)
 *   _avail_backorder  -- backorder text at add-to-cart time    (set by buy-buttons.liquid)
 *
 * Path 1: product page qty selector
 *   quantity-selector:update -> update hidden input value before form submit.
 *
 * Path 2: cart drawer / cart page qty change
 *   window.fetch interceptor catches /cart/change.js requests, reads cached cart state
 *   to compute the correct 'availability' value, and injects it into the same request
 *   body before forwarding to Shopify. The response section HTML therefore contains the
 *   right badge text without any follow-up patch call.
 */
(function () {
  'use strict';

  if (window.__availabilityCheckLoaded) return;
  window.__availabilityCheckLoaded = true;

  const PROP     = 'availability';
  const IN_STOCK = 'Άμεσα Διαθέσιμο';
  const UNAVAILABLE = '❌ Μη διαθέσιμο';

  function asBool(value) {
    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }

  // Save original fetch BEFORE patching. All internal calls use _fetch so they
  // bypass the interceptor and cannot cause a loop.
  const _fetch = window.fetch.bind(window);

  // -- Cart state cache -------------------------------------------------------
  let _cart = null;

  async function getCart() {
    if (_cart) return _cart;
    try {
      const r = await _fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (r.ok) _cart = await r.json();
    } catch {}
    return _cart;
  }

  function invalidateCart() { _cart = null; }

  // -- Availability logic -----------------------------------------------------

  function computeAvail(quantity, props) {
    const stock     = parseInt(props._avail_stock, 10);
    if (isNaN(stock)) return null;
    const backorder = (props._avail_backorder || '').trim();
    const supplierAvailable = asBool(props._avail_supplier_available);
    if (quantity > stock) {
      if (supplierAvailable && backorder) return backorder;
      return UNAVAILABLE;
    }
    if (quantity <= stock && stock > 0) return IN_STOCK;
    return UNAVAILABLE;
  }

  function findItem(cart, id, line) {
    if (!cart) return null;
    if (id != null) {
      const hit = cart.items.find(i => i.key === id || String(i.variant_id) === String(id));
      if (hit) return hit;
    }
    if (line != null) return cart.items[Number(line) - 1] ?? null;
    return null;
  }

  // -- Path 1: product page qty selector -------------------------------------

  document.addEventListener('quantity-selector:update', function (e) {
    if (e.detail?.cartLine != null) return;

    const form = e.target?.closest?.('[data-type="add-to-cart-form"]');
    if (!form) return;

    const inp = form.querySelector('input[name="properties[availability]"][data-avail-stock]');
    if (!inp) return;

    const stock     = parseInt(inp.dataset.availStock, 10);
    if (isNaN(stock)) return;

    const backorder = (inp.dataset.availBackorder || '').trim();
    const unavailable = (inp.dataset.availUnavailable || UNAVAILABLE).trim();
    const supplierAvailable = asBool(inp.dataset.availSupplierAvailable);
    const inStock   = (inp.dataset.availInStock   || IN_STOCK).trim();
    const qtyInput  = e.target.querySelector('[ref="quantityInput"]');
    const cartQty   = parseInt(qtyInput?.getAttribute('data-cart-quantity'), 10) || 0;
    const total     = (parseInt(e.detail.quantity, 10) || 1) + cartQty;

    if (total > stock) {
      inp.value = supplierAvailable && backorder ? backorder : unavailable;
    } else if (total <= stock && stock > 0) {
      inp.value = inStock;
    } else {
      inp.value = unavailable;
    }
  }, { passive: true });

  // -- Path 2: intercept /cart/change.js requests ----------------------------
  // Injecting into the REQUEST (not the response) means the section HTML
  // Shopify returns already has the correct badge -- no extra /cart/change.js
  // call needed, and no DOM re-render race condition.

  window.fetch = async function (...args) {
    let [resource, init] = args;
    const url = typeof resource === 'string' ? resource : (resource?.url ?? '');

    if (/\/cart\/change(?:\.js)?$/.test(url) &&
        String(init?.method || 'GET').toUpperCase() === 'POST') {
      const ct = init?.headers?.['Content-Type'] || init?.headers?.['content-type'] || '';
      if (typeof init?.body === 'string' && ct.includes('application/json')) {
      try {
        const body = JSON.parse(init.body);
        if ((body.id != null || body.line != null) && body.quantity != null) {
          const cart = await getCart();
          const item = findItem(cart, body.id, body.line);
          if (item) {
            const props = item.properties || {};
            const avail = computeAvail(Number(body.quantity), props);
            if (avail !== null) {
              init = { ...init, body: JSON.stringify({ ...body, properties: { ...props, [PROP]: avail } }) };
              args = [resource, init];
            }
          }
        }
      } catch {}
      } // end content-type check
    }

    const res = await _fetch(...args);

    if (res.ok && /\/cart\/(add|change|update)(?:\.js)?$/.test(url)) {
      invalidateCart();
    }
    // Mirror availability property to cart attributes after add/change/update so that
    // Shopify notification emails can read it via {% for attribute in attributes %}.
    // (line.properties is not accessible for custom properties in notification emails.)
    if (res.ok && /\/cart\/(add|change|update)(?:\.js)?$/.test(url)) {
      syncAttrs();
    }

    return res;
  };

  // -- Mirror availability to cart attributes for email notification access --
  // Shopify notification emails cannot read line.properties for custom properties.
  // Cart attributes become order.note_attributes and ARE accessible as `attributes`.

  // /cart/update.js MERGES attributes: keys left out are kept, and a key is deleted
  // only by sending it with an empty value (verified on the live cart 2026-10-31).
  // So the payload carries only the avail_* changes: new/changed values, and ''
  // for keys whose product is no longer in the cart. Other attributes (invoice_*,
  // gclid/fbclid from poas-click-capture.js) are never sent, so they can't be
  // overwritten with a stale copy.
  async function syncAttrs() {
    try {
      const r = await _fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!r.ok) return;
      const cart = await r.json();
      const existing = cart.attributes || {};

      const wanted = {};
      for (const item of cart.items) {
        const avail = (item.properties || {})[PROP];
        if (avail) wanted[`avail_${item.variant_id}`] = String(avail);
      }

      const changes = {};
      for (const [k, v] of Object.entries(existing)) {
        if (k.startsWith('avail_') && !(k in wanted) && String(v ?? '') !== '') changes[k] = '';
      }
      for (const [k, v] of Object.entries(wanted)) {
        if (String(existing[k] ?? '') !== v) changes[k] = v;
      }
      if (Object.keys(changes).length === 0) return;

      await _fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ attributes: changes }),
      });
    } catch {}
  }

  // -- Bootstrap: warm cache + fix items stuck by old buggy sync -------------

  getCart().then(cart => {
    if (!cart) return;

    const stuck = cart.items.filter(item => {
      const p = item.properties || {};
      if (p[PROP] == null) return false;
      const avail = computeAvail(item.quantity, p);
      return avail !== null && avail !== (p[PROP] || '').trim();
    });

    if (stuck.length === 0) return;

    (async () => {
      for (const item of stuck) {
        try {
          const p     = item.properties || {};
          const avail = computeAvail(item.quantity, p);
          await _fetch('/cart/change.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ id: item.key, quantity: item.quantity, properties: { ...p, [PROP]: avail } }),
          });
        } catch {}
      }
      invalidateCart();
      syncAttrs();
    })();
  });

  // Sync on page load to cover carts populated in a prior session.
  syncAttrs();

})();
